// Copyright ©️ 2026 eVolve MEP, LLC

import './jiggle.css';

import { type ReactNode, useState, useRef, useEffect, useEffectEvent } from 'react';

import type { PopoverProps } from '@mantine/core';
import { v4 as uuid } from 'uuid';

import { useModalOpenGuard } from 'components/Mantine/Modals/useModalOpenGuard';
import { useOnConfirmWrapper } from 'components/Mantine/Modals/useOnConfirmWrapper';
import type { SubmitFormEvent } from 'components/Mantine/Modals/WrappedModal';
import type { StrongOmit } from 'helpers/helper-types';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';
import { useIsMobile } from 'hooks/useIsMobile';

import { DesktopPopover } from './DesktopPopover';
import { MobileDrawer } from './MobileDrawer';
import type { PopoverContentProps } from './types';

export type WrappedPopoverProps = {
  opened: boolean;
  /** Fires once per transition to open. */
  onOpen?: () => void;
  onClose: () => void;
  /**
   * Arms the unsaved-changes protection: while true, clicking outside jiggles the dropdown
   * instead of closing it (Desktop only), and in-app navigation is blocked.
   * Does not affect the Confirm button — gate that with `disabled`.
   * @defaultValue false
   */
  isDirty?: boolean;
  /** Sets `disabled` on the Confirm button. */
  disabled?: boolean;
  /** Additional loading state, used in junction with the internal submitting state. */
  loading?: boolean;
  /**
   * Whether or not to close the popover after `onConfirm` completes
   * @defaultValue true
   */
  closeOnConfirm?: boolean;
  title?: string;
  /** @defaultValue "Save" */
  confirmationText?: string;
  /** How many records this confirmation affects (if applicable) */
  confirmationCount?: number;
  /** @defaultValue 300 */
  width?: PopoverProps['width'];
  /** Anchor element. If omitted, an invisible target is rendered (for parent-anchored popovers). */
  target?: ReactNode;
  /** @defaultValue `!isMobile` (the mobile drawer has its own header treatment) */
  withCloseButton?: boolean;
  children: ReactNode;
} & (
  | {
      onConfirm: () => Promise<void> | void;
      formSubmit?: never;
    }
  | {
      onConfirm?: never;
      /** Used by `FormPopover` */
      formSubmit: (e: SubmitFormEvent) => Promise<void> | void;
    }
) &
  StrongOmit<PopoverProps, 'children' | 'opened' | 'onOpen' | 'onClose' | 'onChange'>;

/**
 * A popover for smaller interactions that don't require a full screen Modal.
 *
 * `onConfirm` may be async: a loading overlay shows while it runs, and the popover
 * closes when it resolves (unless `closeOnConfirm` is false).
 *
 * With `isDirty`, will jiggle the dropdown when there are unsaved changes
 * that need the users attention before clicking away.
 *
 * For popovers that collect form input, use `FormPopover` instead.
 */
export const WrappedPopover = (baseProps: WrappedPopoverProps) => {
  const { opened, onOpen, onClose, isDirty = false, onConfirm, formSubmit, target, ...rest } = baseProps;
  const isMobile = useIsMobile();
  const modalOpenGuard = useModalOpenGuard();
  const cachedProps = useRef(rest);
  useEffect(() => {
    // Cache props, except those related to open/close state management to avoid prop clobbering.
    const {
      opened: curOpened,
      onOpen: _1,
      onClose: _2,
      isDirty: _3,
      onConfirm: _4,
      formSubmit: _5,
      target: _6,
      ...curRest
    } = baseProps;
    if (curOpened) cachedProps.current = curRest;
  }, [baseProps]);

  const {
    disabled,
    loading: parentLoading,
    closeOnConfirm = true,
    confirmationText = 'Save',
    confirmationCount,
    width = 300,
    children,
    withCloseButton = !isMobile,
    title,
    withinPortal,
    ...props
  } = opened ? rest : cachedProps.current;

  const [jiggling, setJiggling] = useState(false);

  useEffect(() => {
    if (!opened) return undefined;
    return modalOpenGuard?.register();
  }, [modalOpenGuard, opened]);

  const { submitting, onConfirmWrapper } = useOnConfirmWrapper({ onConfirm, formSubmit, closeOnConfirm, onClose });

  const loading = parentLoading || submitting;

  const onClickClose = () => {
    if (isDirty) {
      setJiggling(true);
    } else {
      onClose();
    }
  };
  const [popoverId] = useState(() => uuid());
  useBlockerWhen(`wrapped-popover-${popoverId}`, isDirty && opened);

  // Popover's built-in `onOpen` fires exactly once, on the transition to open — it never re-fires
  // just because the caller re-rendered. Drawer has no equivalent callback, so this effect replaces
  // it; `useEffectEvent` reads the latest `onOpen` without needing it in the dependency array, which
  // keeps that same "once per open" behavior even when a caller passes an unmemoized inline `onOpen`.
  const onOpenEvent = useEffectEvent(() => onOpen?.());
  useEffect(() => {
    if (opened) {
      setJiggling(false);
      onOpenEvent();
    }
  }, [opened]);

  const contentProps: PopoverContentProps = {
    opened,
    onClose,
    onConfirm: onConfirmWrapper,
    withForm: !!formSubmit,
    disabled,
    loading,
    target,
    confirmationText,
    confirmationCount,
    withCloseButton,
    title,
    withinPortal,
    children,
  };

  return isMobile ? (
    <MobileDrawer {...contentProps} />
  ) : (
    <DesktopPopover
      {...contentProps}
      width={width}
      onClickClose={onClickClose}
      jiggling={jiggling}
      setJiggling={setJiggling}
      {...props}
    />
  );
};
